// src/data/questions/adminQuestions.ts
// Administrative Safeguards Questions (Q11-Q20)
// TODO: Add tooltip content from navigator-improvements-guide/data/tooltip-content-Q11-Q20.md

import { Question } from '../../types';

export const adminQuestions: Question[] = [
  {
    id: 'Q11',
    section: 'admin',
    question: 'Have you conducted a comprehensive HIPAA risk analysis in the last 12 months?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes, comprehensive analysis with documentation', description: '', points: 3 },
      { value: 'partial', label: 'Partial analysis (outdated or incomplete)', description: '', points: 1 },
      { value: 'no', label: 'No formal risk analysis', description: '', points: 0 },
      { value: 'planning', label: 'Planning to start one', description: '', points: 0 },
    ],
    regulation: '§164.308(a)(1)(ii)(A) - Risk analysis (REQUIRED)',
    required: true,
    weight: 3,
    tooltipContent: {
      plainEnglish: "A comprehensive HIPAA risk analysis is your foundational compliance document. It identifies all systems that store/process PHI, assesses vulnerabilities and threats to each system, evaluates existing safeguards, and estimates the likelihood and impact of potential breaches. This isn't a checkbox exercise; it's a serious risk management activity that must be documented, evidence-based, and thorough. A proper risk analysis requires understanding your entire IT ecosystem: which servers host PHI? Which cloud services? Which third-party vendors? What are the known vulnerabilities in these systems? What could a breach cost? OCR uses risk analysis as the baseline for enforcement—if your risk analysis identified a vulnerability and you didn't address it, OCR will cite you for ignoring known risks.",
      hipaaReference: "§164.308(a)(1)(ii)(A) - Risk Analysis (REQUIRED). Status: Required. Effective Date: Security Rule (2003); emphasis on annual updates (2013 Omnibus Rule). 85% of HIPAA breaches could have been prevented by addressing vulnerabilities identified in risk analysis. OCR treats missing or inadequate risk analysis as a foundational failure; often leading to enterprise-level audits.",
      realWorldExample: "A clinic conducted a risk analysis in 2023 that identified unencrypted laptops as a medium risk. In 2024, they conduct a new analysis and find the same unencrypted laptops still exist (plus 3 new ones purchased). This shows they did not remediate the identified risk and are now subject to OCR enforcement. A hospital with a proper risk analysis discovers: 50 active servers, 12 cloud services, 30 third-party vendors with PHI access, and 15 documented vulnerabilities. They prioritize remediation: 3 critical vulnerabilities fixed within 30 days, 7 medium vulnerabilities fixed within 90 days, 5 low vulnerabilities fixed within 6 months.",
      estimatedCost: {
        small: "$5,000-10,000 (DIY with consultant)",
        medium: "$10,000-50,000 (third-party assessment)",
        large: "$50,000-200,000+ (enterprise assessment)"
      },
      implementationSteps: [
        "Initial risk analysis: 4-12 weeks (depending on organization complexity)",
        "Annual update: 2-4 weeks",
        "Remediation planning: 1-2 weeks after analysis complete"
      ],
      commonMistakes: [
        "Conducting risk analysis once and never updating it",
        "Identifying vulnerabilities but not remediating them",
        "Using generic templates without customizing to your environment",
        "Not documenting the analysis methodology and findings"
      ],
      resourceLinks: [
        { title: "HHS Security Risk Assessment Tool", url: "https://www.healthit.gov/topic/privacy-security-and-hipaa/security-risk-assessment-tool", type: "external" },
        { title: "NIST SP 800-30 Risk Assessment Guide", url: "https://csrc.nist.gov/publications/detail/sp/800-30/rev-1/final", type: "external" }
      ],
      timeToComplete: {
        small: "4-6 weeks",
        medium: "6-10 weeks",
        large: "10-16 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "IT Director or CTO",
        "HIPAA Security Official",
        "Compliance Officer",
        "External auditor or consultant (if used)"
      ],
      whereToLook: [
        "Risk analysis reports (dated within last 12 months)",
        "Risk register or vulnerability tracking system",
        "Remediation plans and timelines",
        "Board or management meeting minutes discussing risk findings"
      ],
      howToVerify: "Request the most recent risk analysis report. Check the date (must be within 12 months). Review the scope: does it cover all systems with PHI? Check if identified vulnerabilities have remediation plans with timelines and owners. Ask: 'How do you track remediation progress?'",
      whatToDocument: "Date of last risk analysis, scope covered (systems, vendors, locations), number of vulnerabilities identified, remediation status, next scheduled update"
    }
  },
  {
    id: 'Q12',
    section: 'admin',
    question: 'Do you have documented risk management policies?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes, with risk mitigation plans for identified risks', description: '', points: 3 },
      { value: 'partial', label: 'Partial documentation', description: '', points: 1 },
      { value: 'no', label: 'No formal risk management', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.308(a)(1)(ii)(B) - Risk management (REQUIRED)',
    required: true,
    weight: 3,
    tooltipContent: {
      plainEnglish: "Risk management is the process of addressing vulnerabilities identified in your risk analysis. A documented risk management policy describes: (1) how you prioritize risks (by severity, impact, likelihood), (2) how you decide whether to mitigate, accept, or transfer risks, (3) timelines for remediation, (4) who's responsible, and (5) how you track progress. This policy turns your risk analysis from a one-time document into an ongoing management process. Many organizations skip this step. They conduct a risk analysis, discover vulnerabilities, and then do nothing. When OCR asks 'show us your risk management process,' they have no policy and no evidence of remediation. A documented policy prevents this by establishing a formal process that staff follows and OCR can verify.",
      hipaaReference: "§164.308(a)(1)(ii)(B) - Risk Management (REQUIRED). Status: Required. Effective Date: Security Rule (2003); clarified in 2013 Omnibus Rule. Risk management violations are cited in ~90% of major HIPAA audits (because most orgs don't have formal processes). Without a documented policy, you're vulnerable to negligence claims.",
      realWorldExample: "A clinic identifies 20 vulnerabilities in their risk analysis. Without a risk management policy, they don't know which to fix first. With a policy, they apply a severity score: unencrypted PHI at rest = critical (fix within 30 days); outdated EHR version = medium (fix within 90 days); missing audit logs for one department = low (fix within 6 months). Management allocates resources accordingly. A risk management policy includes criteria for 'risk acceptance'—e.g., 'if a vulnerability affects fewer than 1,000 patients and the cost to remediate exceeds $50,000, we may accept the risk pending cost-benefit analysis.'",
      estimatedCost: {
        small: "$2,000-5,000 (DIY with legal review)",
        medium: "$5,000-10,000 (custom development)",
        large: "$10,000-20,000 (integrated platform)"
      },
      implementationSteps: [
        "Policy development: 2-3 weeks",
        "Risk register setup: 1 week (spreadsheet) to 2-4 weeks (integrated platform)",
        "Training staff on policy: 1-2 weeks"
      ],
      commonMistakes: [
        "Having a risk analysis but no remediation process",
        "Not prioritizing risks (treating all equally)",
        "No tracking system for remediation progress",
        "Not documenting risk acceptance decisions"
      ],
      resourceLinks: [
        { title: "HHS Risk Management Guidance", url: "https://www.hhs.gov/hipaa/for-professionals/security/guidance/guidance-risk-analysis/index.html", type: "external" },
        { title: "NIST Risk Management Framework", url: "https://csrc.nist.gov/projects/risk-management/about-rmf", type: "external" }
      ],
      timeToComplete: {
        small: "2-3 weeks",
        medium: "3-5 weeks",
        large: "5-8 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "HIPAA Security Official",
        "Risk Management Committee",
        "IT Security Manager",
        "Compliance Officer"
      ],
      whereToLook: [
        "Risk management policy document",
        "Risk register or tracking system",
        "Remediation tickets and timelines",
        "Board reports on risk status"
      ],
      howToVerify: "Request the risk management policy. Check if it defines: prioritization criteria, remediation timelines, responsibility assignments, and tracking methods. Ask to see the risk register showing current risks, their priority, and remediation status.",
      whatToDocument: "Policy existence and date, number of active risks being managed, remediation completion rate, risk acceptance decisions"
    }
  },
  {
    id: 'Q13',
    section: 'admin',
    question: 'Is there a designated HIPAA Security Official in your organization?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes, with documented responsibilities', description: '', points: 3 },
      { value: 'unclear', label: 'Yes, but role is not clearly defined', description: '', points: 1 },
      { value: 'no', label: 'No designated official', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.308(a)(2) - Assigned security responsibility (REQUIRED)',
    required: true,
    weight: 3,
    tooltipContent: {
      plainEnglish: "A HIPAA Security Official (also called Chief Information Security Officer or CISO at larger orgs) is the person responsible for implementing and maintaining the HIPAA Security Rule. This role must have documented responsibilities, authority to implement controls, and direct or indirect oversight of security functions. For a small clinic, this might be a part-time role held by the IT manager or practice administrator. For a hospital, it's typically a dedicated position. The key is clarity: everyone must know who the Security Official is, what they're responsible for, and how to escalate security issues to them. OCR will ask 'who is your Security Official?' and will expect to interview that person and review their documented responsibilities.",
      hipaaReference: "§164.308(a)(2) - Assigned Security Responsibility (REQUIRED). Status: Required. Effective Date: Security Rule (2003). Absence of a named Security Official is cited in ~70% of HIPAA audits of small organizations. Without clear responsibility, security initiatives stall and vulnerabilities aren't addressed.",
      realWorldExample: "A 20-person clinic designates the IT manager as Security Official but never gives them a job description or formal authority. When OCR asks 'what are your responsibilities?', the IT manager says 'I'm not sure, nobody really told me.' Non-compliant. A hospital creates a formal CISO position with a written charter: 'Responsible for implementing the HIPAA Security Rule, conducting risk analyses, overseeing audit logging, managing incident response, etc.' The CISO attends monthly security committee meetings, presents quarterly reports to the board, and has authority to approve/deny access requests. Compliant.",
      estimatedCost: {
        small: "$0 (designate existing staff) to $2,000 (consultant to define role)",
        medium: "$80,000-120,000/year (part-time role, 0.5-1 FTE)",
        large: "$150,000-250,000/year (dedicated CISO)"
      },
      implementationSteps: [
        "Designation: 1 day",
        "Role definition + documentation: 1-2 weeks",
        "Authority delegation and communication: 1 week"
      ],
      commonMistakes: [
        "Designating someone without giving them authority or resources",
        "Not documenting the role responsibilities",
        "Security Official unaware of their responsibilities",
        "No reporting structure or oversight"
      ],
      resourceLinks: [
        { title: "HHS Security Official Requirements", url: "https://www.hhs.gov/hipaa/for-professionals/security/laws-regulations/index.html", type: "external" }
      ],
      timeToComplete: {
        small: "1-2 weeks",
        medium: "2-3 weeks",
        large: "3-4 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "Executive leadership (CEO, COO)",
        "HR department",
        "IT Director",
        "The person designated as Security Official"
      ],
      whereToLook: [
        "Organizational chart",
        "Job descriptions",
        "Security committee meeting minutes",
        "Policy documents naming the Security Official"
      ],
      howToVerify: "Ask: 'Who is your HIPAA Security Official?' Request their job description or charter. Interview the person to confirm they understand their responsibilities. Check if they have authority to implement security controls.",
      whatToDocument: "Name and title of Security Official, documented responsibilities, reporting structure, date of designation"
    }
  },
  {
    id: 'Q14',
    section: 'admin',
    question: 'Do you have workforce security policies (authorization, clearance, termination)?',
    type: 'single',
    options: [
      { value: 'comprehensive', label: 'Yes, comprehensive policies for all workforce changes', description: '', points: 3 },
      { value: 'partial', label: 'Partial policies', description: '', points: 1 },
      { value: 'no', label: 'No formal policies', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.308(a)(3) - Workforce security',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: "Workforce security includes three components: (1) authorization—ensuring each employee's access is appropriate for their role, (2) clearance—vetting employees before they access PHI (background checks, credentialing), and (3) termination—promptly revoking access when employees leave. This prevents unvetted or terminated employees from accessing PHI. Many organizations do nothing for authorization (everyone gets the same access level), skip background checks entirely, or leave terminated employees' access active for months. You must have documented policies for each component. For example: 'All employees undergo background checks before starting. Access is provisioned based on job description (minimum necessary). When employees leave, IT revokes all access within 24 hours, with manager verification.'",
      hipaaReference: "§164.308(a)(3) - Workforce Security (includes authorization, clearance, and termination). Status: Required. Effective Date: Security Rule (2003). 25% of HIPAA breaches involve insider threats (rogue employees, terminated staff, contractors with old access). Background check omissions are commonly cited as violations; average fine: $10,000-50,000.",
      realWorldExample: "A clinic hires a new front desk receptionist without a background check. The person has a prior conviction for fraud. Months later, they access patient records to steal insurance information. Clinic is liable for negligence. A hospital implements a proper policy: background check before day 1, access provisioned after IT receives approval, automated termination workflow so access is revoked within 2 hours of termination date. When an employee is terminated, their manager confirms in the system, IT revokes all access, and IT verifies revocation the next day.",
      estimatedCost: {
        small: "$30-150 per employee (background checks)",
        medium: "$10,000-50,000 setup + $1,000-5,000/month (identity management)",
        large: "$50,000-100,000 setup + $5,000-15,000/month (enterprise IAM)"
      },
      implementationSteps: [
        "Policy development: 2 weeks",
        "Background check vendor integration: 2-4 weeks",
        "Identity management system: 4-8 weeks (if new)",
        "Staff training: 1 week"
      ],
      commonMistakes: [
        "Skipping background checks to save money",
        "Not revoking access promptly when employees leave",
        "Giving everyone the same access level (not role-based)",
        "No verification that access was actually revoked"
      ],
      resourceLinks: [
        { title: "HHS Workforce Security Guidance", url: "https://www.hhs.gov/hipaa/for-professionals/security/guidance/index.html", type: "external" }
      ],
      timeToComplete: {
        small: "4-6 weeks",
        medium: "8-12 weeks",
        large: "12-16 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "HR Director",
        "IT Security Manager",
        "Background check vendor contact",
        "Identity management system administrator"
      ],
      whereToLook: [
        "Workforce security policy document",
        "Background check records",
        "Access provisioning/deprovisioning logs",
        "Offboarding checklists"
      ],
      howToVerify: "Request the workforce security policy. Check if it covers authorization, clearance, and termination. Ask: 'Show me background check records for recent hires.' Review access logs to verify terminated employees no longer have access.",
      whatToDocument: "Policy existence, background check completion rate, average time to revoke access after termination, number of terminated employees with lingering access"
    }
  },
  {
    id: 'Q15',
    section: 'admin',
    question: 'Do you provide annual HIPAA security awareness training to all workforce members?',
    type: 'single',
    options: [
      { value: 'annual', label: 'Yes, annual training with documentation/attestation', description: '', points: 3 },
      { value: 'sporadic', label: 'Sporadic training, not annual', description: '', points: 1 },
      { value: 'no', label: 'No formal training program', description: '', points: 0 },
      { value: 'onlynew', label: 'Only for new hires', description: '', points: 1 },
    ],
    regulation: '§164.308(a)(5) - Security awareness and training',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: "All employees must receive annual HIPAA security training, not just IT staff. This covers privacy rules, security best practices (strong passwords, phishing awareness, physical security), incident reporting, and your organization's specific policies. Training must be documented, with attendance records and attestations. You must track who has been trained and when, and enforce training completion before employees can access PHI. Annual means at least once per calendar year, but best practice is more frequent (quarterly or quarterly + ad-hoc for new threats). New employees must be trained before accessing PHI. If you skip training for any employee group or just assume 'everyone knows HIPAA,' you're non-compliant.",
      hipaaReference: "§164.308(a)(5) - Security Awareness and Training. Status: Required. Effective Date: Security Rule (2003). Phishing is the #1 initial vector for healthcare breaches; training reduces phishing success by 50-80%. OCR mandates training as a corrective action in almost every audit; failure to implement is a separate violation.",
      realWorldExample: "A clinic purchases a HIPAA training module, requires all staff to complete it by December 31 each year, and tracks completion. Staff who don't complete training lose system access January 1. Compliant. An office manager attends a phishing simulation test and clicks a malicious link. The simulation shows they were vulnerable. They complete additional phishing awareness training and future simulations show improvement. A clinic provides only one training in the clinic's history and assumes everyone is trained forever. OCR audit shows 30% of current staff were hired after the training and have never received HIPAA training. Non-compliant.",
      estimatedCost: {
        small: "$10-50/user/year (training platform)",
        medium: "$15-30/user/year (500+ users)",
        large: "$5,000-20,000/year (in-person + platform)"
      },
      implementationSteps: [
        "Platform selection and setup: 2-3 weeks",
        "Content customization: 1-2 weeks",
        "Staff training + completion: 4-6 weeks"
      ],
      commonMistakes: [
        "Training only once and never again",
        "Not tracking completion",
        "Skipping certain employee groups",
        "No consequences for non-completion"
      ],
      resourceLinks: [
        { title: "HHS Security Training Requirements", url: "https://www.hhs.gov/hipaa/for-professionals/security/laws-regulations/index.html", type: "external" }
      ],
      timeToComplete: {
        small: "6-8 weeks",
        medium: "8-10 weeks",
        large: "10-12 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "HR Director",
        "Compliance Officer",
        "Training platform administrator",
        "Department managers"
      ],
      whereToLook: [
        "Training completion records",
        "Training platform reports",
        "Attestation forms",
        "Training schedule/calendar"
      ],
      howToVerify: "Request training completion reports for the last 12 months. Check: What percentage of employees completed training? When was the last training? Are new hires trained before accessing PHI?",
      whatToDocument: "Training completion rate, date of last training, training platform used, attestation records"
    }
  },
  {
    id: 'Q16',
    section: 'admin',
    question: 'Do you have security incident response procedures?',
    type: 'single',
    options: [
      { value: 'documented', label: 'Yes, documented procedures with response team', description: '', points: 3 },
      { value: 'informal', label: 'Informal procedures', description: '', points: 1 },
      { value: 'no', label: 'No incident response plan', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.308(a)(6) - Security incident procedures (REQUIRED)',
    required: true,
    weight: 3,
    tooltipContent: {
      plainEnglish: "When a security incident occurs—whether a breach, system outage, malware infection, or unauthorized access—you need a documented procedure to respond quickly and effectively. The procedure should define: (1) who to contact immediately (incident response team), (2) what to do first (isolate systems? preserve evidence? notify leadership?), (3) investigation steps, (4) when to notify patients/OCR/law enforcement, and (5) post-incident review. An incident response procedure isn't just for breaches; it covers all security incidents. Your procedure should specify what counts as a 'breach' (actually disclosed PHI) vs. a 'security incident' (potential exposure without known disclosure). Without a documented procedure, staff will panic, make mistakes, or fail to preserve evidence for investigation.",
      hipaaReference: "§164.308(a)(6) - Security Incident Procedures (REQUIRED). Status: Required; Breach Notification Rule (§164.400-414) specifies breach-specific procedures. Effective Date: Security Rule (2003); clarified in Breach Notification Rule (2009). Incident response determines the scope of the breach: contained quickly = minimal exposure; left uncontained = maximum damage.",
      realWorldExample: "A clinic's EHR is hacked. Instead of a documented procedure, staff email 'help, we've been hacked' to everyone. Months pass before they understand the breach scope. Patient notification is delayed, OCR fines them for late notification. With a proper procedure: (1) IT isolates the affected system within 1 hour, (2) incident response team meets within 2 hours, (3) forensic investigation begins within 24 hours, (4) investigation is complete within 2 weeks, (5) breach is announced at day 15 (within 60-day deadline).",
      estimatedCost: {
        small: "$3,000-7,000 (plan development)",
        medium: "$10,000-20,000 (plan + team training + retainer)",
        large: "$20,000-50,000 (comprehensive program)"
      },
      implementationSteps: [
        "Plan development: 3-4 weeks",
        "Team training + tabletop exercise: 1-2 weeks",
        "Tool/system integration: 1-2 weeks"
      ],
      commonMistakes: [
        "No documented procedure (staff improvise during incidents)",
        "Not defining who's on the incident response team",
        "No practice drills or tabletop exercises",
        "Confusing security incidents with breaches"
      ],
      resourceLinks: [
        { title: "HHS Breach Notification Rule", url: "https://www.hhs.gov/hipaa/for-professionals/breach-notification/index.html", type: "external" },
        { title: "NIST Incident Response Guide", url: "https://csrc.nist.gov/publications/detail/sp/800-61/rev-2/final", type: "external" }
      ],
      timeToComplete: {
        small: "4-6 weeks",
        medium: "6-8 weeks",
        large: "8-12 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "HIPAA Security Official",
        "IT Security Manager",
        "Incident response team members",
        "Legal counsel"
      ],
      whereToLook: [
        "Incident response plan document",
        "Incident tracking system",
        "Past incident reports",
        "Tabletop exercise records"
      ],
      howToVerify: "Request the incident response plan. Check if it defines: response team, contact procedures, investigation steps, notification timelines. Ask: 'When was the last incident? How was it handled?' Review incident logs.",
      whatToDocument: "Plan existence and date, incident response team members, number of incidents in last 12 months, response times"
    }
  },
  {
    id: 'Q17',
    section: 'admin',
    question: 'Do you have contingency plans (data backup, disaster recovery, emergency operations)?',
    type: 'single',
    options: [
      { value: 'all', label: 'Yes, all three components tested annually', description: '', points: 3 },
      { value: 'partial', label: 'Partial plans (e.g., backups only)', description: '', points: 1 },
      { value: 'no', label: 'No contingency plans', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.308(a)(7) - Contingency plan (REQUIRED)',
    required: true,
    weight: 3,
    tooltipContent: {
      plainEnglish: "Contingency planning ensures you can continue operations and access patient data if something goes wrong (hardware failure, natural disaster, cyberattack, pandemic, etc.). This requires three components: (1) data backup—regular copies of data stored separately, tested for restore capability, (2) disaster recovery—procedures to restore systems and data after a major outage, and (3) emergency operations—how you continue caring for patients if your main systems are down (paper records, phone consultations, etc.). Many organizations have backups but don't test them. You must test backups at least annually to confirm they can actually be restored. You must have timelines: how quickly can you restore critical systems? (e.g., 'within 4 hours' or 'within 24 hours').",
      hipaaReference: "§164.308(a)(7) - Contingency Plan (REQUIRED) includes: Data backup, Disaster recovery plan, Emergency mode operations plan. Status: Required. Effective Date: Security Rule (2003); testing emphasized in 2013 Omnibus Rule. 40% of healthcare organizations lack tested disaster recovery plans; when disaster strikes, they're unprepared.",
      realWorldExample: "A clinic backs up data daily to an external drive kept in the office. When a fire destroys the office, the backup is also destroyed. No backup off-site. Compliant clinic: backup to AWS (off-site), test restores monthly, document restore times, have a plan for emergency operations (paper charts, phone consultations if EHR is down). A hospital discovers a ransomware infection. With tested disaster recovery, they restore from a clean backup from 6 hours earlier, restore within 2 hours, lose 1 day of data. Without backup, they either pay ransom or lose 6 months of data.",
      estimatedCost: {
        small: "$500-2,000/month (cloud backup)",
        medium: "$20,000-50,000 setup + $2,000-10,000/month (backup + DR site)",
        large: "$50,000-150,000 setup + $10,000-30,000/month (hot standby)"
      },
      implementationSteps: [
        "Initial assessment and plan: 4-6 weeks",
        "Backup implementation: 2-8 weeks",
        "Disaster recovery setup: 4-12 weeks",
        "Testing and drills: ongoing"
      ],
      commonMistakes: [
        "Having backups but never testing them",
        "Keeping backups in the same location as primary data",
        "No documented RTO/RPO (recovery time/point objectives)",
        "No emergency operations plan for manual workflows"
      ],
      resourceLinks: [
        { title: "HHS Contingency Planning Guidance", url: "https://www.hhs.gov/hipaa/for-professionals/security/guidance/index.html", type: "external" },
        { title: "NIST Contingency Planning Guide", url: "https://csrc.nist.gov/publications/detail/sp/800-34/rev-1/final", type: "external" }
      ],
      timeToComplete: {
        small: "6-10 weeks",
        medium: "10-16 weeks",
        large: "16-24 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "IT Director",
        "Backup administrator",
        "Disaster recovery coordinator",
        "Operations manager"
      ],
      whereToLook: [
        "Contingency plan document",
        "Backup logs and test results",
        "Disaster recovery procedures",
        "Emergency operations plan"
      ],
      howToVerify: "Request the contingency plan. Check if it covers all three components: backup, DR, emergency ops. Ask: 'When was the last backup test? What were the results? What's your RTO/RPO?' Review test documentation.",
      whatToDocument: "Plan existence, last backup test date and results, RTO/RPO targets, emergency operations procedures"
    }
  },
  {
    id: 'Q18',
    section: 'admin',
    question: 'How often do you test your backup and disaster recovery procedures?',
    type: 'single',
    options: [
      { value: 'quarterly', label: 'Quarterly or more frequently', description: '', points: 3 },
      { value: 'annually', label: 'Annually', description: '', points: 2 },
      { value: 'rarely', label: 'Rarely or never', description: '', points: 0 },
      { value: 'none', label: 'We have no testing procedures', description: '', points: 0 },
    ],
    regulation: '§164.308(a)(7)(ii)(D) - Testing and revision (Addressable)',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: "Testing is critical—untested backups are worthless. Backups fail silently: you think they're working, but when you try to restore, nothing happens. You must test backups at minimum annually, and best practice is quarterly. Testing means: (1) perform a restore from backup, (2) verify the restored data is complete and uncorrupted, (3) measure how long restoration takes, (4) document results. For disaster recovery, testing means simulating an outage and verifying you can failover to the DR system successfully. Many organizations skip testing because it's time-consuming and disruptive. But OCR mandates it as part of the Contingency Plan rule. Testing also catches problems before a real disaster strikes.",
      hipaaReference: "§164.308(a)(7)(ii) - Contingency Plan testing; §164.308(a)(7)(ii)(D) - Testing and Revision (Addressable but required to address). Status: Required. Effective Date: Security Rule (2003); testing emphasis (2013). 35% of backup test failures reveal problems that would cause major outages if not fixed. OCR specifically asks for testing documentation.",
      realWorldExample: "A clinic backs up data nightly and assumes it works. In year 2 of operation, they test and discover the backup license expired 18 months ago; no backups have been created since. Disaster averted by testing. A hospital tests quarterly: Q1 test succeeds in 3 hours, Q2 test succeeds in 4 hours, Q3 test fails (corrupted backup data), Q4 test succeeds after fixing the backup software. By tracking trends, they catch the Q3 problem and investigate root cause.",
      estimatedCost: {
        small: "$2,000-10,000/year (automated testing tools)",
        medium: "$5,000-15,000/year (test lab + staff time)",
        large: "$10,000-30,000/year (comprehensive DR testing)"
      },
      implementationSteps: [
        "Establish testing schedule: 1 week",
        "Quarterly test: 4-8 hours each",
        "Annual DR test: 8-16 hours"
      ],
      commonMistakes: [
        "Never testing backups",
        "Testing only once and assuming it always works",
        "Not documenting test results",
        "Not measuring restore times"
      ],
      resourceLinks: [
        { title: "HHS Contingency Plan Testing", url: "https://www.hhs.gov/hipaa/for-professionals/security/guidance/index.html", type: "external" }
      ],
      timeToComplete: {
        small: "2-3 weeks (setup)",
        medium: "3-4 weeks (setup)",
        large: "4-6 weeks (setup)"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "IT Director",
        "Backup administrator",
        "System administrators",
        "DR coordinator"
      ],
      whereToLook: [
        "Backup test logs",
        "Test result documentation",
        "Restore time measurements",
        "Testing schedule/calendar"
      ],
      howToVerify: "Request backup test results for the last 12 months. Check: How often are tests performed? What were the results? Were any failures identified and fixed? What are the measured restore times?",
      whatToDocument: "Testing frequency, last test date, test results (pass/fail), restore times, issues identified and resolved"
    }
  },
  {
    id: 'Q19',
    section: 'admin',
    question: 'Do you conduct periodic HIPAA compliance evaluations?',
    type: 'single',
    options: [
      { value: 'thirdparty', label: 'Yes, at least annually with third-party audits', description: '', points: 3 },
      { value: 'internal', label: 'Internal evaluations only', description: '', points: 2 },
      { value: 'no', label: 'No regular evaluations', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.308(a)(8) - Evaluation (REQUIRED)',
    required: true,
    weight: 3,
    tooltipContent: {
      plainEnglish: "A periodic evaluation (also called a compliance audit or compliance assessment) reviews your entire HIPAA compliance program. It assesses: (1) policies and procedures (are they current and effective?), (2) controls (are they implemented and working?), (3) workforce knowledge (do staff understand HIPAA?), (4) documentation (do you have evidence of compliance?), and (5) risk management (have identified risks been addressed?). Evaluations can be internal (your own staff) or external (third-party auditors). OCR prefers third-party audits because they're more objective. Internal audits are better than nothing but should be reinforced with periodic third-party reviews. Frequency should be at least annual, with more frequent assessments for high-risk areas.",
      hipaaReference: "§164.308(a)(8) - Evaluation (REQUIRED). Status: Required; annual minimum. Effective Date: Security Rule (2003). 75% of HIPAA violations go undetected until OCR audits because organizations skip self-assessments. Regular evaluations allow you to fix problems before regulatory scrutiny.",
      realWorldExample: "A clinic conducts an internal assessment annually: checklist review, staff interviews, spot checks of access controls and audit logs. They find and fix problems before OCR sees them. A hospital conducts internal assessments quarterly and a third-party audit annually. Third-party audit finds 3 major issues the internal team missed (due to bias or lack of expertise). Hospital remediates and uses findings to improve internal processes. A small clinic can't afford a third-party auditor; they conduct a thorough DIY assessment using HHS checklist, document findings, and track remediation. Still compliant (though third-party would be better).",
      estimatedCost: {
        small: "$4,000-10,000/year (internal evaluation)",
        medium: "$5,000-15,000 (third-party audit for small org)",
        large: "$20,000-200,000+ (enterprise third-party audit)"
      },
      implementationSteps: [
        "Internal evaluation: 4-8 weeks",
        "Third-party audit: 4-12 weeks",
        "Remediation planning: 1-4 weeks after findings"
      ],
      commonMistakes: [
        "Never conducting evaluations",
        "Conducting evaluation once and never again",
        "Not documenting findings",
        "Not remediating identified issues"
      ],
      resourceLinks: [
        { title: "HHS Audit Protocol", url: "https://www.hhs.gov/hipaa/for-professionals/compliance-enforcement/audit/protocol/index.html", type: "external" }
      ],
      timeToComplete: {
        small: "4-8 weeks",
        medium: "6-12 weeks",
        large: "12-16 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "Compliance Officer",
        "HIPAA Security Official",
        "Internal audit team",
        "Third-party auditor (if used)"
      ],
      whereToLook: [
        "Evaluation/audit reports",
        "Remediation action plans",
        "Evaluation schedule",
        "SOC 2 reports (if applicable)"
      ],
      howToVerify: "Request the most recent evaluation report. Check: When was it conducted? Was it internal or third-party? What findings were identified? What remediation actions were taken?",
      whatToDocument: "Last evaluation date, type (internal/external), findings count, remediation status, next scheduled evaluation"
    }
  },
  {
    id: 'Q20',
    section: 'admin',
    question: 'Do you have signed Business Associate Agreements (BAAs) with all vendors handling PHI?',
    type: 'single',
    options: [
      { value: 'all', label: 'Yes, signed BAAs with all vendors', description: '', points: 3 },
      { value: 'most', label: 'Most vendors, but some missing', description: '', points: 1 },
      { value: 'no', label: 'No BAAs in place', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure which vendors need BAAs', description: '', points: 0 },
    ],
    regulation: '§164.308(b)(1) - Business associate contracts (REQUIRED)',
    required: true,
    conditionalOn: { questionId: 'Q1', requiredValue: ['provider', 'plan', 'clearinghouse'] },
    weight: 3,
    tooltipContent: {
      plainEnglish: "A Business Associate Agreement is a contract between a Covered Entity and a vendor (Business Associate) that specifies the vendor's obligations under HIPAA. If your vendor accesses, uses, or stores PHI on your behalf, you need a signed BAA. Examples: EHR vendors, cloud providers, billing companies, telehealth platforms, IT support, email providers, document management services, etc. The BAA specifies: (1) what data the vendor can access, (2) permitted uses (only for healthcare operations, not data mining or marketing), (3) security obligations (encryption, audit logging, incident reporting), (4) breach notification procedures, and (5) termination requirements (data destruction or return). Without a BAA, you're liable if the vendor breaches; with a BAA, the vendor shares some liability.",
      hipaaReference: "§164.308(b)(1) - Business Associate Contracts (REQUIRED). Status: Required if you have vendors with PHI access. Effective Date: Privacy Rule (2003); clarified in HITECH Act (2009) and 2013 Omnibus Rule. Missing BAAs are cited in ~85% of HIPAA audits (most common violation). If a vendor without a BAA breaches patient data, you're fully liable.",
      realWorldExample: "A clinic uses Google Workspace for email. Google's HIPAA BAA is easily obtained (online). Clinic signs it. Clinic uses a custom patient portal built by a local IT vendor. Vendor initially refuses to sign a BAA ('we don't work with healthcare'), but clinic requires it as a condition of payment. Vendor eventually signs. Clinic's EHR vendor provides a BAA with the contract. Years later, clinic discovers an old business process (faxes sent to a service) never got a BAA; they immediately terminate the service or obtain a retroactive BAA.",
      estimatedCost: {
        small: "$0 (most vendors include BAAs at no cost)",
        medium: "$500-2,000 (legal review of BAA terms)",
        large: "$3,000-5,000 (consultant to manage vendor BAAs)"
      },
      implementationSteps: [
        "BAA request to vendor: 1-2 weeks (often immediate)",
        "Legal review: 1-2 weeks",
        "Ongoing vendor management: 5-10 hours/year per vendor"
      ],
      commonMistakes: [
        "Not identifying all vendors with PHI access",
        "Assuming vendors don't need BAAs",
        "Not tracking BAA expiration dates",
        "Using vendors before BAA is signed"
      ],
      resourceLinks: [
        { title: "HHS Business Associate Requirements", url: "https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/business-associates/index.html", type: "external" },
        { title: "Sample BAA Template", url: "https://www.hhs.gov/hipaa/for-professionals/covered-entities/sample-business-associate-agreement-provisions/index.html", type: "external" }
      ],
      timeToComplete: {
        small: "2-4 weeks",
        medium: "4-6 weeks",
        large: "6-8 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "Procurement/purchasing department",
        "IT Director",
        "Compliance Officer",
        "Legal counsel"
      ],
      whereToLook: [
        "BAA library or repository",
        "Vendor contracts",
        "Vendor management spreadsheet",
        "Procurement records"
      ],
      howToVerify: "Request a list of all vendors with PHI access. For each vendor, ask: 'Do we have a signed BAA?' Review the BAA library to confirm all vendors are covered. Check expiration dates.",
      whatToDocument: "Number of vendors with PHI access, number with signed BAAs, any missing BAAs, BAA expiration dates"
    }
  },
];
